import React, { useState } from "react";
import { ArrowLeftIcon, LockIcon, Trash2Icon, UserPlusIcon } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useProject } from "../../data/project";
import { useCurrentOrgUser, useOrg } from "../../auth/org-provider";
import { Switch } from "../../components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableRow } from "../../components/ui/table";
import { AvatarForUser, UserAvatarAndName } from "../../components/shared/org/avatar-for-user";
import { Button, buttonVariants } from "../../components/ui/button";
import { cn } from "../../lib/utils";
import { UserDropdown } from "../../components/shared/org/user-dropdown";
import { PopoverTrigger } from "../../components/ui/popover";
import { useConfirmDialog } from "../../components/modals/confirmation-dialog-provider";
import { useAddProjectCollaboratorMutation } from "../../data/mutations/add-project-collaborator";
import { useRemoveProjectCollaboratorMutation } from "../../data/mutations/remove-project-collaborator";
import { useTransferProjectOwnershipMutation, useUpdateProjectMutation } from "../../data/mutations/update-project";
import { useMakeProjectPublicMutation } from "../../data/mutations/make-project-public";
import Spinner from "../../components/ui/spinner";
import { ContentSpacer } from "../common/layout";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

export function ProjectSettingsPage() {
    const { project, refetch: refetchProject } = useProject();
    const currentUser = useCurrentOrgUser();
    const org = useOrg();
    const navigate = useNavigate();
    const [transferState, setTransferState] = useState<{ userId: string; isPrivate: boolean } | null>(null);

    const isPrivate = Array.isArray(project.collaborators);
    const collaboratorIds = project.collaborators ?? [];

    const isOwner = project.creatorId === currentUser.id;
    const isCollaborator = isPrivate && collaboratorIds.includes(currentUser.id);
    const canView = isOwner || isCollaborator;
    const canEdit = isOwner;

    const addCollaboratorMutation = useAddProjectCollaboratorMutation();
    const removeCollaboratorMutation = useRemoveProjectCollaboratorMutation();
    const makePublicMutation = useMakeProjectPublicMutation();
    const updateProjectMutation = useUpdateProjectMutation();
    const transferOwnershipMutation = useTransferProjectOwnershipMutation();
    const confirm = useConfirmDialog();

    const handleTogglePrivate = (checked: boolean) => {
        if (!canEdit) return;
        if (checked) {
            addCollaboratorMutation.mutate([currentUser.id]);
        } else {
            confirm(
                {
                    title: "Change Project Visibility",
                    body: (
                        <div className="flex flex-col gap-3 pb-3">
                            <div>
                                Are you sure you want to change the project's visibility to <strong>public</strong>?
                            </div>
                            <div className="text-sm text-muted-foreground">
                                This would make the project visible to all members of your organization.
                            </div>
                        </div>
                    ),
                    confirmButtonText: "Confirm",
                },
                async () => {
                    await makePublicMutation.mutateAsync();
                }
            )();
        }
    };

    const handleAddCollaborator = async (userId: string) => {
        if (!canEdit) return;
        await addCollaboratorMutation.mutateAsync([userId]);
    };

    const handleChangeOwner = async (userId: string) => {
        if (!canEdit) return;
        const newOwner = org.users.find((u) => u.id === userId);
        if (!newOwner) return;

        confirm(
            {
                title: "Transfer Project Ownership",
                body: (
                    <div className="flex flex-col gap-3 pb-3">
                        <div>
                            Are you sure you want to transfer ownership to{" "}
                            <strong className="font-semibold">{newOwner.name}</strong>?
                        </div>
                        <div className="text-sm text-muted-foreground">
                            You will lose administrative privileges for this project.
                        </div>
                    </div>
                ),
                confirmButtonText: "Transfer",
                confirmButtonVariant: "destructive",
            },
            async () => {
                await transferOwnershipMutation.mutateAsync(userId);
                setTransferState({ userId: newOwner.id, isPrivate });
                refetchProject();
            }
        )();
    };

    const closeTransferModal = () => {
        setTransferState(null);
        navigate(`/project/${project.id}`, { replace: true });
    };

    const handleTransferModalOpenChange = (open: boolean) => {
        if (!open) {
            closeTransferModal();
        }
    };

    const availableUsers = org.users.filter((u) => !collaboratorIds.includes(u.id));
    const ownershipTransferCandidates = org.users.filter((u) => {
        if (u.id === project.creatorId) return false;
        if (isPrivate) return collaboratorIds.includes(u.id);
        return true;
    });

    const collaborators = collaboratorIds
        .map((id) => org.users.find((u) => u.id === id))
        .filter((u): u is (typeof org.users)[0] => u !== undefined);

    const owner = org.users.find((u) => u.id === project.creatorId);

    const isPending =
        addCollaboratorMutation.isPending ||
        removeCollaboratorMutation.isPending ||
        makePublicMutation.isPending ||
        updateProjectMutation.isPending ||
        transferOwnershipMutation.isPending;

    const disableEdits = !canEdit || isPending;

    if (!canView && !transferState) {
        return (
            <ContentSpacer>
                <div className="mx-auto flex min-h-[65vh] max-w-2xl flex-col items-center justify-center gap-3 p-8 text-center">
                    <LockIcon className="h-10 w-10" />
                    <h1>Project owner access only</h1>
                    <p>Only the project owner can access settings.</p>
                    <p className="max-w-xl text-muted-foreground">
                        If you need to make changes, please contact the project owner.
                    </p>
                    <Button className="mt-4" onClick={() => navigate(`/project/${project.id}`)}>
                        Back to project overview
                    </Button>
                </div>
            </ContentSpacer>
        );
    }

    return (
        <ContentSpacer>
            <Link
                to=".."
                className="group inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground transition-all hover:-translate-x-1 hover:text-foreground"
            >
                <ArrowLeftIcon className="h-4 w-4 transition-all group-hover:translate-x-0.5" />
                Back to project
            </Link>

            <h2 className="mb-4 text-2xl font-medium">Project Settings</h2>

            {!canEdit && (
                <div className="rounded-lg border border-dashed bg-muted/30 p-4 text-sm text-muted-foreground">
                    You are viewing settings as a collaborator. Only the project owner can make changes.
                </div>
            )}

            <h3>General</h3>
            <hr />
            <div className="flex items-center justify-between rounded-lg border bg-card p-4">
                <div>
                    <div className="font-medium">Private Project</div>
                    <div className="text-sm text-muted-foreground">Only collaborators can view this project</div>
                </div>
                <Switch checked={isPrivate} onCheckedChange={handleTogglePrivate} disabled={disableEdits} />
            </div>

            <hr />
            <h3>Project Owner</h3>
            <div className="flex items-center justify-between rounded-lg border bg-card p-4">
                <div className="flex items-center gap-3">
                    {owner && <AvatarForUser user={owner} className="h-10 w-10" />}
                    <div className="font-medium">{owner?.name ?? "Unknown User"}</div>
                </div>
                <UserDropdown users={ownershipTransferCandidates} onSelect={handleChangeOwner}>
                    <PopoverTrigger asChild>
                        <Button variant="outline" disabled={disableEdits || ownershipTransferCandidates.length === 0}>
                            Transfer Ownership
                        </Button>
                    </PopoverTrigger>
                </UserDropdown>
            </div>

            {isPrivate && (
                <>
                    <hr />
                    <div className="flex flex-row items-center gap-4">
                        <h3>Collaborators</h3>
                        <UserDropdown users={availableUsers} onSelect={handleAddCollaborator}>
                            <PopoverTrigger asChild>
                                <div
                                    className={cn(
                                        buttonVariants({ size: "sm" }),
                                        (disableEdits || availableUsers.length === 0) &&
                                            "pointer-events-none opacity-50",
                                        "cursor-pointer"
                                    )}
                                >
                                    <UserPlusIcon className="me-2 h-4 w-4" />
                                    Add collaborator
                                </div>
                            </PopoverTrigger>
                        </UserDropdown>
                    </div>
                    <Table>
                        <TableBody>
                            <TableRow>
                                <TableHead className="w-[200px]">Name</TableHead>
                                <TableHead className="w-[200px]">Title</TableHead>
                                <TableHead>Email</TableHead>
                                <TableHead></TableHead>
                            </TableRow>
                            {collaborators.map((u) => (
                                <CollaboratorRow
                                    key={u.id}
                                    user={u}
                                    isCreator={u.id === project.creatorId}
                                    onRemove={() => removeCollaboratorMutation.mutate(u.id)}
                                    disabled={disableEdits}
                                    canEdit={canEdit}
                                />
                            ))}
                        </TableBody>
                    </Table>
                </>
            )}

            <Dialog open={!!transferState} onOpenChange={handleTransferModalOpenChange}>
                <DialogContent className="sm:max-w-[420px]">
                    <div className="flex flex-col items-center text-center">
                        <DialogHeader>
                            <DialogTitle>Ownership transferred successfully</DialogTitle>
                        </DialogHeader>
                        {transferState && (
                            <div className="mt-6 flex flex-col items-center gap-3">
                                <div className="flex flex-col items-center gap-2">
                                    <UserAvatarAndName
                                        avatarClassName="h-12 w-12"
                                        nameClassName="text-lg font-semibold text-foreground"
                                        userId={transferState.userId}
                                    />
                                </div>
                                <p className="text-md text-muted-foreground">
                                    is now the owner of this project.
                                    {transferState.isPrivate ? " You'll remain a collaborator." : ""}
                                </p>
                            </div>
                        )}
                        <div className="mt-8 flex justify-center">
                            <Button onClick={closeTransferModal}>Go back to project</Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </ContentSpacer>
    );
}

function CollaboratorRow({
    user,
    isCreator,
    onRemove,
    disabled,
    canEdit,
}: {
    user: { id: string; name: string; title?: string; email: string };
    isCreator: boolean;
    onRemove: () => void;
    disabled: boolean;
    canEdit: boolean;
}) {
    const confirm = useConfirmDialog();

    const handleRemoveClick = confirm(
        {
            title: "Remove Collaborator",
            body: (
                <div className="flex flex-col gap-3 pb-3">
                    <div>
                        Remove <strong className="font-semibold">{user.name}</strong> from this project?
                    </div>
                    <div className="text-sm text-muted-foreground">
                        They will no longer have access to this project.
                    </div>
                </div>
            ),
            confirmButtonText: "Remove",
            confirmButtonVariant: "destructive",
        },
        onRemove
    );

    return (
        <TableRow>
            <TableCell>
                <div className="flex flex-row items-center gap-2">
                    <AvatarForUser className="h-8 w-8" user={user} />
                    {user.name}
                </div>
            </TableCell>
            <TableCell className="text-muted-foreground">
                {user.title ? user.title : <span className="opacity-45">&mdash;</span>}
            </TableCell>
            <TableCell>{user.email}</TableCell>
            <TableCell className="text-right">
                {!isCreator && canEdit && (
                    <Button
                        size="sm"
                        variant="ghost"
                        onClick={handleRemoveClick}
                        disabled={disabled}
                        className="text-muted-foreground hover:text-destructive"
                    >
                        {disabled ? <Spinner className="h-4 w-4" /> : <Trash2Icon className="h-4 w-4" />}
                    </Button>
                )}
            </TableCell>
        </TableRow>
    );
}
